import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getClientState,
  getAllClientStates,
  updateClientState,
  deleteClientState,
} from '~apis/user/clientState';

export const ClientStateQueryKey = {
  default: () => ['clientState'],
  detail: (name: string) => [...ClientStateQueryKey.default(), name],
  list: () => [...ClientStateQueryKey.default(), 'list'],
};

export function useGetClientState(name: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ClientStateQueryKey.detail(name),
    queryFn: () => getClientState(name),
    enabled: options?.enabled ?? !!name,
    refetchOnMount: true,
  });
}

export function useGetAllClientStates() {
  return useQuery({
    queryKey: ClientStateQueryKey.list(),
    queryFn: () => getAllClientStates(),
    refetchOnMount: true,
  });
}

export function useUpdateClientState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) =>
      updateClientState(name, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ClientStateQueryKey.detail(variables.name),
      });
      queryClient.invalidateQueries({
        queryKey: ClientStateQueryKey.list(),
      });
    },
  });
}

export function useDeleteClientState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => deleteClientState(name),
    onSuccess: (_, name) => {
      queryClient.invalidateQueries({
        queryKey: ClientStateQueryKey.detail(name),
      });
      queryClient.invalidateQueries({
        queryKey: ClientStateQueryKey.list(),
      });
    },
  });
}
